import {createContext, use, useMemo, useState, type Dispatch, type SetStateAction} from 'react'
import type {LogEntry, Session} from '../types/session'
import type {PublicUser} from '../types/public-user'

interface SessionContextType {
  session: Session
  sessionOwner: PublicUser | undefined
  logs?: LogEntry[]
  logsAreLoading?: boolean
  mcpToolNameMappings: Record<string, string>
  setMcpToolNameMappings: Dispatch<SetStateAction<Record<string, string>>>
}

export const SessionContext = createContext<SessionContextType | undefined>(undefined)

export function useSessionContext() {
  const context = use(SessionContext)
  if (!context) {
    throw new Error('useSessionContext must be used within a SessionContextProvider')
  }
  return context
}

interface SessionContextProviderProps {
  session: Session
  sessionOwner: PublicUser | undefined
  logs?: LogEntry[]
  logsAreLoading?: boolean
  children: React.ReactNode
}

export function SessionContextProvider({
  session,
  sessionOwner,
  logs,
  logsAreLoading,
  children,
}: SessionContextProviderProps) {
  const [mcpToolNameMappings, setMcpToolNameMappings] = useState<Record<string, string>>({})
  const value = useMemo(
    () => ({session, sessionOwner, logs, logsAreLoading, mcpToolNameMappings, setMcpToolNameMappings}),
    [session, sessionOwner, logs, logsAreLoading, mcpToolNameMappings],
  )
  return <SessionContext value={value}>{children}</SessionContext>
}
